import type { Player } from '../types/player';

export type RankSortField = keyof Player | 'rank';
export type RankSortDirection = 'asc' | 'desc';

export interface RankingsQuery {
  searchQuery: string;
  positionFilter: string;
  sortField?: RankSortField | null;
  sortDirection?: RankSortDirection;
}

export function nextSortState(
  currentField: RankSortField | null,
  currentDirection: RankSortDirection,
  clicked: RankSortField
): { field: RankSortField | null; direction: RankSortDirection } {
  if (currentField === clicked) {
    if (currentDirection === 'asc') {
      return { field: clicked, direction: 'desc' };
    }
    return { field: null, direction: 'asc' };
  }
  return { field: clicked, direction: 'asc' };
}

function sortValue(player: Player, field: RankSortField): string | number {
  switch (field) {
    case 'rank':
      return player.rank ?? Number.POSITIVE_INFINITY;
    case 'name':
      return player.name.toLowerCase();
    case 'position':
      return player.position;
    case 'team':
      return player.team;
    case 'age':
      return player.age ?? Number.POSITIVE_INFINITY;
    case 'projection':
      return player.projection ?? player.projectedPoints ?? 0;
    case 'value':
      return player.value ?? 0;
    case 'lastYearPoints':
      return player.lastYearPoints ?? 0;
    case 'vorp':
      return player.vorp ?? 0;
    default:
      return 0;
  }
}

export function filterAndSortPlayers(
  players: Player[],
  query: RankingsQuery
): Player[] {
  const search = query.searchQuery.trim().toLowerCase();
  const positionFilter = query.positionFilter;
  const sortField = query.sortField ?? null;
  const sortDirection = query.sortDirection ?? 'asc';

  return players
    .filter((player) => {
      const nameMatch = player.name.toLowerCase().includes(search);
      const positionMatch = positionFilter === 'All' || player.position === positionFilter;
      return nameMatch && positionMatch;
    })
    .sort((a, b) => {
      if (!sortField) {
        return (a.rank ?? 0) - (b.rank ?? 0);
      }

      const valueA = sortValue(a, sortField);
      const valueB = sortValue(b, sortField);

      if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
}

export const RANKINGS_PAGE_SIZE = 100;

export function paginatePlayers<T>(
  items: T[],
  page: number,
  pageSize: number = RANKINGS_PAGE_SIZE
): {
  items: T[];
  page: number;
  pageCount: number;
  total: number;
  start: number;
  end: number;
} {
  const total = items.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize) || 1);
  const current = Math.min(Math.max(1, page), pageCount);
  const startIndex = (current - 1) * pageSize;
  const slice = items.slice(startIndex, startIndex + pageSize);
  return {
    items: slice,
    page: current,
    pageCount,
    total,
    start: total === 0 ? 0 : startIndex + 1,
    end: Math.min(startIndex + pageSize, total),
  };
}
